import * as ents from '@attain-apps/entities'
import * as graphql from '@attain-apps/fe/web/main/lib/graphql'
import { Range } from '@attain-apps/range'
import * as transformers from '@attain-apps/transformers'
import { TypeAsserter } from '@attain-apps/type-guards'
import gql from 'graphql-tag'

import * as enums from '../enums'

export const DOC = gql`
  fragment FragmentProcessEssentials on ProcessEssentials {
    activities {
      execution {
        status
        timeframe {
          end
          start
        }
        detail
      }
      item {
        type
        id
      }
      type
      id
      extra
    }
    execution {
      status
      timeframe {
        end
        start
      }
      detail
    }
    appId
    zoneId
    id
    items {
      id
      name
      type
    }
    scenarioId
    templateId
    tenantId
  }
`

const PROCESS_ITEM_TYPE = transformers.combine(
  graphql.transformers.OUT.enumeration<
    graphql.schema.ProcessItemType,
    ents.core.relay.processes.Item.Type
  >({
    [graphql.schema.ProcessItemType.Allocation]:
      ents.core.relay.processes.Item.Type.Allocation,
    [graphql.schema.ProcessItemType.Entity]:
      ents.core.relay.processes.Item.Type.RecordModel,
    [graphql.schema.ProcessItemType.Importer]:
      ents.core.relay.processes.Item.Type.Importer,
    [graphql.schema.ProcessItemType.Pool]:
      ents.core.relay.processes.Item.Type.Pool,
    [graphql.schema.ProcessItemType.Report]:
      ents.core.relay.processes.Item.Type.Report,
    [graphql.schema.ProcessItemType.Unknown]:
      ents.core.relay.processes.Item.Type.Unknown,
  }),
)

export const TRANSFORMER = graphql.transformers.OUT.object<
  graphql.schema.FragmentProcessEssentialsFragment,
  ents.core.relay.processes.Process.Essentials
>({
  activityExecutions: {
    // FIXME: this should use record transformer instead of "custom"
    // TODO: Below seems really wrong, and has some issue with types.
    usingFullInput: graphql.transformers.OUT.default(
      graphql.transformers.OUT.select(
        'activities',
        graphql.transformers.OUT.custom(activities =>
          activities?.reduce<
            Partial<
              Record<
                ents.core.relay.processes.Activity.Execution.Id,
                ents.core.relay.processes.Activity.Execution
              >
            >
          >((prev, current) => {
            const item =
              current?.item?.id && current.item.type
                ? {
                    id: current?.item?.id,
                    type: TypeAsserter.validateButJustWarn(
                      PROCESS_ITEM_TYPE.getEntityFrom(current?.item?.type),
                      TypeAsserter.assertIsNotNullish,
                    ),
                  }
                : undefined
            if (current?.id) {
              Object.assign(prev, {
                [current.id]: {
                  detail: current.execution?.detail ?? undefined,
                  extra: current.extra ?? undefined,
                  item: ents.core.relay.processes.Item.Reference.isReference(
                    item,
                  )
                    ? item
                    : undefined,
                  status: TypeAsserter.validateButJustWarn(
                    enums.executionStatus.TRANSFORMER.getEntityFrom(
                      current.execution?.status,
                    ),
                    TypeAsserter.assertIsNotNullish,
                  ),
                  timeframe: current.execution?.timeframe
                    ? new Range<Date>({
                        end: current.execution.timeframe.end
                          ? new Date(current.execution.timeframe.end)
                          : undefined,
                        start: current.execution.timeframe.start
                          ? new Date(current.execution.timeframe.start)
                          : undefined,
                      })
                    : new Range<Date>(),
                  type: TypeAsserter.validateButJustWarn(
                    enums.activityType.TRANSFORMER.getEntityFrom(current.type),
                    TypeAsserter.assertIsNotNullish,
                  ),
                } satisfies ents.core.relay.processes.Activity.Execution,
              })
            }
            return prev
          }, {}),
        ),
      ),
      {},
    ),
  },
  appId: transformers.PASS_THROUGH,
  detail: {
    usingFullInput: graphql.transformers.OUT.select(
      'execution',
      graphql.transformers.OUT.select('detail', transformers.PASS_THROUGH),
    ),
  },
  id: graphql.transformers.OUT.validate(
    transformers.PASS_THROUGH,
    TypeAsserter.assertIsNotNullish,
    { warnOnly: true },
  ),
  items: graphql.transformers.OUT.default(
    graphql.transformers.OUT.list(
      graphql.transformers.OUT.object({
        id: graphql.transformers.OUT.validate(
          transformers.PASS_THROUGH,
          TypeAsserter.assertIsNotNullish,
          { warnOnly: true },
        ),
        name: graphql.transformers.OUT.validate(
          transformers.PASS_THROUGH,
          TypeAsserter.assertIsNotNullish,
          { warnOnly: true },
        ),
        type: graphql.transformers.OUT.default(
          enums.itemType.TRANSFORMER,
          ents.core.relay.processes.Item.Type.Unknown,
        ),
      }),
      { onNullish: 'remove' },
    ),
    [],
  ),
  scenarioId: transformers.PASS_THROUGH,
  status: {
    usingFullInput: graphql.transformers.OUT.select(
      'execution',
      graphql.transformers.OUT.select(
        'status',
        graphql.transformers.OUT.validate(
          enums.executionStatus.TRANSFORMER,
          TypeAsserter.assertIsNotNullish,
          { warnOnly: true },
        ),
      ),
    ),
  },
  templateId: graphql.transformers.OUT.validate(
    transformers.PASS_THROUGH,
    TypeAsserter.assertIsNotNullish,
    { warnOnly: true },
  ),
  tenantId: graphql.transformers.OUT.validate(
    transformers.PASS_THROUGH,
    TypeAsserter.assertIsNotNullish,
    { warnOnly: true },
  ),

  timeframe: {
    usingFullInput: graphql.transformers.OUT.default(
      graphql.transformers.OUT.select(
        'execution',
        graphql.transformers.OUT.select(
          'timeframe',
          graphql.transformers.OUT.custom(
            timeframe =>
              new Range<Date>({
                end: timeframe?.end ? new Date(timeframe.end) : undefined,
                start: timeframe?.start ? new Date(timeframe.start) : undefined,
              }),
          ),
        ),
      ),
      new Range<Date>(),
    ),
  },
  zoneId: graphql.transformers.OUT.validate(
    transformers.PASS_THROUGH,
    TypeAsserter.assertIsNotNullish,
    { warnOnly: true },
  ),
})
